import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Mail, Smartphone, ShieldCheck, FileText } from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const LoginPage = () => {
  const { login, checkAuth } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const nextPath = searchParams.get('next') || '';
  const isRTL = language === 'ar';

  // 'phone' (phone + password — primary patient/pharmacist login)
  // 'email' (staff)
  // NOTE: OTP/Twilio flow is temporarily hidden from the UI while SMS
  // credentials are being finalised. The backend endpoints and this file's
  // OTP handlers are intentionally kept intact so we can flip
  // `SHOW_OTP_TAB` back to `true` once Twilio is wired.
  const SHOW_OTP_TAB = false;
  const [mode, setMode] = useState('phone');

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // OTP state
  const [otpPhone, setOtpPhone] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0); // seconds remaining
  const [verifying, setVerifying] = useState(false);       // guards double-submit

  // Countdown ticker for the resend cooldown.
  React.useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const id = setTimeout(() => setResendCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearTimeout(id);
  }, [resendCooldown]);

  // Phone + password state
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');

  // Email + password state
  const [emailValue, setEmailValue] = useState('');
  const [passwordEmail, setPasswordEmail] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);

  const routeAfterLogin = (user) => {
    if (user.role === 'admin') navigate('/admin');
    else if (user.role === 'doctor') navigate('/doctor');
    else if (user.role === 'pharmacist') navigate('/pharmacist');
    else if (user.role === 'physio_manager') navigate('/physio-manager');
    else if (nextPath && nextPath.startsWith('/')) navigate(nextPath, { replace: true });
    else navigate('/');
  };

  // ---- OTP flow ----
  const handleSendOtp = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setError('');
    // Server-side cooldown mirror — never fire while a countdown is running.
    if (loading || resendCooldown > 0) return;
    const trimmed = (otpPhone || '').trim();
    if (!trimmed.startsWith('+') || trimmed.length < 8) {
      setError(t(
        'الرجاء إدخال رقم هاتف صحيح بصيغة دولية (مثل +9617…)',
        'Please enter a valid phone number in international format (e.g. +9617…)',
        'Numéro international requis (+…)'
      ));
      return;
    }
    setLoading(true);
    // A brand-new OTP request invalidates any stale code the user might
    // still have in the input.
    setOtpCode('');
    try {
      const r = await axios.post(`${API}/auth/patient/otp/send`, { phone_number: trimmed }, { withCredentials: true });
      setOtpSent(true);
      setResendCooldown(Number(r.data?.cooldown_seconds) || 20);
    } catch (err) {
      const status = err.response?.status;
      const retry = err.response?.headers?.['retry-after'];
      if (status === 429 && retry) {
        setResendCooldown(Number(retry) || 20);
      }
      setError(err.response?.data?.detail || t('تعذر إرسال رمز التحقق', 'Failed to send code', 'Échec envoi code'));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setError('');
    if (verifying || loading) return; // hard guard against double-submit
    if (!otpCode || otpCode.length < 4) {
      setError(t('أدخل رمز التحقق الصحيح', 'Enter the verification code', 'Entrez le code'));
      return;
    }
    setVerifying(true);
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/auth/patient/otp/verify`,
        { phone_number: otpPhone.trim(), code: otpCode.trim() },
        { withCredentials: true }
      );
      // Refresh auth context user then route.
      await checkAuth();
      if (!res.data?.profile_complete) {
        navigate('/patient?welcome=1', { replace: true });
      } else if (nextPath && nextPath.startsWith('/')) {
        navigate(nextPath, { replace: true });
      } else {
        navigate('/', { replace: true });
      }
    } catch (err) {
      setError(err.response?.data?.detail || t('رمز غير صحيح', 'Invalid code', 'Code invalide'));
    } finally {
      setLoading(false);
      setVerifying(false);
    }
  };

  // ---- Phone + password flow ----
  const handlePhoneLogin = async (e) => {
    e.preventDefault();
    setError('');
    if (!phone.trim() || !password) {
      setError(t('الرجاء إدخال رقم الهاتف وكلمة المرور', 'Please enter phone and password', 'Téléphone et mot de passe requis'));
      return;
    }
    setLoading(true);
    try {
      const user = await login(phone.trim(), password);
      routeAfterLogin(user);
    } catch (err) {
      setError(err.response?.data?.detail || t('حدث خطأ في تسجيل الدخول', 'Login failed', 'Échec'));
    } finally {
      setLoading(false);
    }
  };

  // ---- Email + password flow ----
  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setError('');
    if (!acceptedTerms) {
      setError(t('يجب الموافقة على شروط الأطباء للمتابعة', 'You must accept the doctor terms to continue', 'Acceptez les conditions'));
      return;
    }
    setLoading(true);
    try {
      const user = await login(emailValue, passwordEmail, { accepted_doctor_terms: true });
      routeAfterLogin(user);
    } catch (err) {
      setError(err.response?.data?.detail || t('حدث خطأ في تسجيل الدخول', 'Login failed', 'Échec'));
    } finally {
      setLoading(false);
    }
  };

  const switchMode = (m) => {
    setMode(m);
    setError('');
    // Reset OTP flow if leaving it
    if (m !== 'otp') { setOtpSent(false); setOtpCode(''); }
  };

  return (
    <div className={`min-h-screen bg-gradient-to-b from-blue-50 to-white flex items-center justify-center px-4 py-8 ${isRTL ? 'rtl' : 'ltr'}`}>
      <Card className="w-full max-w-md p-8">
        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold text-[#2c3e50] mb-2">{t('تسجيل الدخول', 'Login', 'Connexion')}</h1>
          <p className="text-gray-600 text-sm">
            {t('سجل دخولك للاستفادة من جميع خدماتنا', 'Login to access all our services', 'Connectez-vous pour accéder à nos services')}
          </p>
        </div>

        {/* Mode switcher — OTP tab is currently hidden (see SHOW_OTP_TAB) */}
        <div
          className={`grid ${SHOW_OTP_TAB ? 'grid-cols-3' : 'grid-cols-2'} gap-1 bg-gray-100 rounded-xl p-1 mb-6`}
          data-testid="login-mode-tabs"
        >
          {SHOW_OTP_TAB && (
            <button
              type="button"
              onClick={() => switchMode('otp')}
              data-testid="tab-otp"
              className={`py-2 rounded-lg text-xs font-bold flex items-center justify-center gap-1 transition
                ${mode === 'otp' ? 'bg-white shadow text-[#1e88e5]' : 'text-gray-500 hover:text-gray-700'}`}
            >
              <ShieldCheck size={14} />
              {t('برمز SMS', 'By SMS', 'Par SMS')}
            </button>
          )}
          <button
            type="button"
            onClick={() => switchMode('phone')}
            data-testid="tab-phone"
            className={`py-2 rounded-lg text-xs font-bold flex items-center justify-center gap-1 transition
              ${mode === 'phone' ? 'bg-white shadow text-[#1e88e5]' : 'text-gray-500 hover:text-gray-700'}`}
          >
            <Smartphone size={14} />
            {t('بالهاتف', 'By Phone', 'Téléphone')}
          </button>
          <button
            type="button"
            onClick={() => switchMode('email')}
            data-testid="tab-email"
            className={`py-2 rounded-lg text-xs font-bold flex items-center justify-center gap-1 transition
              ${mode === 'email' ? 'bg-white shadow text-[#1e88e5]' : 'text-gray-500 hover:text-gray-700'}`}
          >
            <Mail size={14} />
            {t('بالبريد', 'By Email', 'E-mail')}
          </button>
        </div>

        {error && (
          <Alert variant="destructive" className="mb-4" data-testid="login-error-alert">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* OTP flow (currently hidden — gated by SHOW_OTP_TAB) */}
        {SHOW_OTP_TAB && mode === 'otp' && !otpSent && (
          <form onSubmit={handleSendOtp} className="space-y-5" data-testid="form-send-otp">
            <div>
              <Label htmlFor="otp_phone" className="text-base">{t('رقم الهاتف', 'Phone Number', 'Téléphone')}</Label>
              <Input
                id="otp_phone"
                type="tel"
                value={otpPhone}
                onChange={(e) => setOtpPhone(e.target.value)}
                placeholder="+96170123456"
                required
                data-testid="otp-phone-input"
                className="mt-2"
              />
              <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
                <ShieldCheck size={12} className="text-emerald-500" />
                {t('سنرسل رمز تحقق مؤلف من 6 أرقام عبر رسالة نصية',
                  "We'll send a 6-digit code by SMS",
                  'Un code de 6 chiffres sera envoyé par SMS')}
              </p>
            </div>
            <Button
              type="submit"
              disabled={loading || resendCooldown > 0}
              className="w-full bg-[#1e88e5] hover:bg-[#1976d2] py-6 text-lg font-bold rounded-xl"
              data-testid="send-otp-button"
            >
              {loading
                ? t('جاري الإرسال...', 'Sending...', 'Envoi...')
                : resendCooldown > 0
                  ? t(`انتظر ${resendCooldown} ث`, `Wait ${resendCooldown}s`, `${resendCooldown}s`)
                  : t('إرسال رمز التحقق', 'Send code', 'Envoyer')}
            </Button>
          </form>
        )}

        {SHOW_OTP_TAB && mode === 'otp' && otpSent && (
          <form onSubmit={handleVerifyOtp} className="space-y-5" data-testid="form-verify-otp">
            <p className="text-base text-gray-700">
              {t('أدخل الرمز المرسل إلى', 'Enter the code sent to', 'Entrez le code envoyé à')}{' '}
              <span className="font-bold text-[#0d47a1] tracking-wider" dir="ltr">{otpPhone}</span>
            </p>
            <div>
              <Label htmlFor="otp_code" className="text-base">{t('رمز التحقق', 'Verification Code', 'Code')}</Label>
              <Input
                id="otp_code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                placeholder="123456"
                required
                data-testid="otp-input"
                className="mt-2 text-center text-3xl tracking-[0.5em] font-bold py-6 h-16 border-2 rounded-xl"
              />
            </div>
            <Button
              type="submit"
              disabled={loading || verifying}
              className="w-full bg-[#1e88e5] hover:bg-[#1976d2] py-6 text-lg font-bold rounded-xl"
              data-testid="verify-otp-button"
            >
              {loading ? t('جاري التحقق...', 'Verifying...', 'Vérification...') : t('تأكيد', 'Verify', 'Vérifier')}
            </Button>
            <div className="flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={handleSendOtp}
                disabled={loading || resendCooldown > 0}
                className="text-sm text-[#1565c0] font-bold hover:underline disabled:text-gray-400 disabled:no-underline disabled:cursor-not-allowed"
                data-testid="resend-otp-button"
              >
                {resendCooldown > 0
                  ? t(`إعادة الإرسال بعد ${resendCooldown} ث`, `Resend in ${resendCooldown}s`, `Renvoyer dans ${resendCooldown}s`)
                  : t('إعادة إرسال الرمز', 'Resend code', 'Renvoyer le code')}
              </button>
              <button
                type="button"
                onClick={() => { setOtpSent(false); setOtpCode(''); setError(''); setResendCooldown(0); }}
                className="text-sm text-gray-500 hover:text-gray-700 underline underline-offset-2"
                data-testid="change-phone-button"
              >
                {t('تغيير الرقم', 'Change number', 'Changer')}
              </button>
            </div>
          </form>
        )}

        {/* Phone + password flow */}
        {mode === 'phone' && (
          <form onSubmit={handlePhoneLogin} className="space-y-5" data-testid="form-phone-login">
            <div>
              <Label htmlFor="phone_number" className="text-base">{t('رقم الهاتف', 'Phone Number', 'Téléphone')}</Label>
              <Input
                id="phone_number"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder={t('أدخل رقم هاتفك', 'Enter your phone number', 'Entrez votre téléphone')}
                required
                data-testid="phone-input"
                className="mt-2"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-base">{t('كلمة المرور', 'Password', 'Mot de passe')}</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('أدخل كلمة المرور', 'Enter your password', 'Entrez le mot de passe')}
                required
                data-testid="password-input"
                className="mt-2"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#1e88e5] hover:bg-[#1976d2] py-6 text-lg font-bold rounded-xl"
              data-testid="login-submit-button"
            >
              {loading ? t('جاري تسجيل الدخول...', 'Logging in...', 'Connexion...') : t('تسجيل الدخول', 'Login', 'Connexion')}
            </Button>
            <div className="text-center">
              <Link to="/forgot-password" className="text-sm text-[#1e88e5] hover:underline" data-testid="forgot-password-link">
                {t('نسيت كلمة المرور؟', 'Forgot password?', 'Mot de passe oublié ?')}
              </Link>
            </div>
          </form>
        )}

        {/* Email + password flow */}
        {mode === 'email' && (
          <form onSubmit={handleEmailLogin} className="space-y-5" data-testid="form-email-login">
            <div>
              <Label htmlFor="email">{t('البريد الإلكتروني', 'Email', 'E-mail')}</Label>
              <Input
                id="email"
                type="email"
                value={emailValue}
                onChange={(e) => setEmailValue(e.target.value)}
                placeholder="staff@example.com"
                required
                data-testid="email-input"
                className="mt-2"
              />
            </div>
            <div>
              <Label htmlFor="password_email">{t('كلمة المرور', 'Password', 'Mot de passe')}</Label>
              <Input
                id="password_email"
                type="password"
                value={passwordEmail}
                onChange={(e) => setPasswordEmail(e.target.value)}
                placeholder={t('أدخل كلمة المرور', 'Enter your password', 'Entrez le mot de passe')}
                required
                data-testid="password-email-input"
                className="mt-2"
              />
            </div>
            <div className="flex items-start gap-3 p-3 rounded-lg bg-blue-50 border border-blue-100">
              <Checkbox
                id="doctor_terms"
                checked={acceptedTerms}
                onCheckedChange={(v) => setAcceptedTerms(!!v)}
                data-testid="accept-doctor-terms-checkbox"
                className="mt-1"
              />
              <label htmlFor="doctor_terms" className="text-sm text-gray-700 leading-relaxed cursor-pointer">
                {t('أوافق على ', 'I agree to the ', 'J’accepte les ')}
                <button
                  type="button"
                  onClick={() => setTermsOpen(true)}
                  className="text-[#1e88e5] hover:underline font-bold inline-flex items-center gap-1"
                  data-testid="view-doctor-terms-button"
                >
                  <FileText size={14} />
                  {t('شروط الأطباء', "doctor's terms", 'conditions médecins')}
                </button>
              </label>
            </div>
            <Button
              type="submit"
              disabled={loading || !acceptedTerms}
              className="w-full bg-[#1e88e5] hover:bg-[#1976d2]"
              data-testid="login-email-submit-button"
            >
              {loading ? t('جاري تسجيل الدخول...', 'Logging in...', 'Connexion...') : t('تسجيل الدخول', 'Login', 'Connexion')}
            </Button>
            <div className="text-center">
              <p className="text-xs text-gray-500" data-testid="staff-password-admin-note">
                {t(
                  'حسابات الأطباء والصيادلة: يرجى التواصل مع الإدارة لتعيين كلمة مرور جديدة.',
                  'Staff accounts: contact the administrator to set a new password.',
                  'Comptes du personnel : contactez l’administrateur.',
                )}
              </p>
            </div>
          </form>
        )}

        {(mode === 'otp' || mode === 'phone') && (
          <div className="mt-6 text-center">
            <p className="text-gray-600 text-sm">
              {t('جديد على طبابة؟', 'New to Tababa?', 'Nouveau sur Tababa ?')}{' '}
              <Link to="/register" className="text-[#1e88e5] hover:underline font-semibold" data-testid="register-link">
                {t('أنشئ حساباً بكلمة مرور', 'Create an account with password', 'Créer un compte')}
              </Link>
            </p>
          </div>
        )}
      </Card>

      <Dialog open={termsOpen} onOpenChange={setTermsOpen}>
        <DialogContent className="max-w-4xl h-[85vh] flex flex-col" data-testid="doctor-terms-dialog">
          <DialogHeader>
            <DialogTitle>{t('شروط الأطباء - عقد أون لاين', "Doctor's Terms")}</DialogTitle>
          </DialogHeader>
          <iframe
            src={`${process.env.REACT_APP_BACKEND_URL}/api/legal/doctor-terms`}
            title="Doctor Terms PDF"
            className="w-full flex-1 border-0 rounded"
            data-testid="doctor-terms-pdf-iframe"
          />
          <div className="flex justify-between items-center pt-3 border-t">
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/legal/doctor-terms`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-[#1e88e5] hover:underline"
              data-testid="download-terms-link"
            >
              {t('فتح في نافذة جديدة', 'Open in new window')}
            </a>
            <Button
              onClick={() => { setAcceptedTerms(true); setTermsOpen(false); }}
              className="bg-[#1e88e5] hover:bg-[#1976d2]"
              data-testid="accept-and-close-terms-button"
            >
              {t('أوافق على الشروط', 'I agree to the terms')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LoginPage;
